<script setup>
import { useMonitoringAutofill } from "@/features/monitoring/useMonitoringAutofill.js";
import { onBeforeMount, ref, computed, onMounted, toValue } from "vue";
import { HAS_EXITED_DELETE_FLOW } from "@/scripts/userFlags";
import store from "@/store";
import { PH_EVENT_USER_VIEWED_DD_SUBMISSION_FORM } from "@/scripts/posthogEvents.js";
import { useColorScheme } from "@/composables/useColorScheme";
import { posthogCapture } from "@/scripts/posthog.js";
import EnrollmentCardHeader from "@/features/enrollment/EnrollmentCardHeader.vue";

const { colorScheme } = useColorScheme();

onMounted(() =>
  posthogCapture(PH_EVENT_USER_VIEWED_DD_SUBMISSION_FORM, {
    theme: colorScheme.value,
  })
);

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  isEnrolled: {
    type: Boolean,
    default: false,
  },
});

const autofillData = ref({
  wasAutofilled: false,
});

const {
  combineAutofillData,
  getAutofillDataFromEnrollment,
  getAutofillDataFromSearchResultsV2,
  getAutofillDataFromUserObject,
} = useMonitoringAutofill();

const autofillFormOnce = async () => {
  if (autofillData.value.wasAutofilled) {
    return;
  }

  let newAutofillData = toValue(autofillData);
  const dataFromSearchResults = await getAutofillDataFromSearchResultsV2();
  newAutofillData = await combineAutofillData(
    newAutofillData,
    dataFromSearchResults
  );

  const dataFromUserObject = await getAutofillDataFromUserObject();
  newAutofillData = await combineAutofillData(
    newAutofillData,
    dataFromUserObject
  );

  const dataFromEnrollment = await getAutofillDataFromEnrollment();
  newAutofillData = await combineAutofillData(
    newAutofillData,
    dataFromEnrollment
  );
  autofillData.value = { ...newAutofillData, wasAutofilled: true };
};

onBeforeMount(autofillFormOnce);

const hasExitedDeleteFlow = computed(() => {
  return store.getters.getFlag(HAS_EXITED_DELETE_FLOW);
});
</script>

<template>
  <div class="enrollment-v2-enroll">
    <div class="enrollment-v2-enroll__content">
      <EnrollmentCardHeader class="enrollment-v2-enroll__header" />
      <router-view
        v-if="!props.isLoading"
        :is-loading="props.isLoading"
        :is-enrolled="props.isEnrolled"
        :has-exited-delete-flow="hasExitedDeleteFlow"
        :autofill-data="autofillData"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.enrollment-v2-enroll {
  display: flex;
  height: 100%;
  padding: 0 8px 8px;
  overflow: hidden;
  position: relative;

  @media (width >= 1024px) {
    padding: 0 8px 8px 0;
    gap: 8px;
  }

  &__header {
    max-width: 400px;
    margin: 0 auto;
  }

  &__content {
    flex: 1;
    border-radius: 20px;
    padding: 16px;
    overflow-y: auto;

    @include custom-scroll-bar;

    position: relative;
    z-index: 1;
    width: 100%;
    transition: all 0.4s ease;
    background-color: $color-primary-5;
  }

  &__orb {
    transition: all 0.5s ease;
    width: 400px;
    max-width: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;

    &--animate {
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.5s ease;
    }
  }
}
</style>
